import React from 'react';
import { Card, CardContent, Typography, Box, Stack, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom Tooltip for better UI styling matching MUI
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          p: 1.5,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
          {label} ({payload[0].payload.day})
        </Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

const AttendanceChart = ({ apiResponse }) => {
  // Safe check if data exists
  if (!apiResponse || !apiResponse.status || !apiResponse.data) {
    return (
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No attendance data found.</Typography>
      </Card>
    );
  }

  const { filter, data } = apiResponse;

  // Format filter text for display (e.g., "last_7_days" -> "Last 7 Days")
  const formatFilterLabel = (str) => {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Card 
      sx={{ 
        maxWidth: 700, 
        mx: 'auto', 
        mt: 4, 
        boxShadow: '0px 4px 20px rgba(0,0,0,0.05)', 
        borderRadius: 3,
        overflow: 'visible'
      }}
    >
      <CardContent>
        {/* Header Section */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              Staff Attendance Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Managed Staff: {data[0]?.total_staff || 0}
            </Typography>
          </Box>
          <Chip 
            label={formatFilterLabel(filter)} 
            color="primary" 
            variant="outlined" 
            size="small" 
            sx={{ fontWeight: 'bold' }}
          />
        </Stack>

        {/* Chart Container */}
        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(tick) => {
                  // Shorten dates to MM-DD format for cleaner look
                  const parts = tick.split('-');
                  return parts.length === 3 ? `${parts[1]}-${parts[2]}` : tick;
                }}
                tick={{ fill: '#757575', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                domain={[0, 'dataMax']} 
                tick={{ fill: '#757575', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36} 
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '13px', fontWeight: 500 }}
              />
              {/* Present Bar (Green tint) */}
              <Bar 
                name="Present" 
                dataKey="present" 
                fill="#2e7d32" 
                radius={[4, 4, 0, 0]} 
                maxBarSize={40}
              />
              {/* Absent Bar (Red tint) */}
              <Bar 
                name="Absent" 
                dataKey="absent" 
                fill="#d32f2f" 
                radius={[4, 4, 0, 0]} 
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AttendanceChart;